import React, { useRef } from "react";
import { typography, spacing } from "../styles/designSystem";
import {
  clamp,
  getScrollVisibility,
  useScrollProgress,
} from "../styles/animations";
import { whyO3SItems } from "../data/aboutData";

const WhyO3S = () => {
  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef);
  const visibility = getScrollVisibility(progress);

  return (
    <section
      id="why-o3s"
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-b from-[#11130f] via-[#171a14] to-[#11130f] py-24 text-white md:py-32 lg:py-40"
    >
      {/* Section accents */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/25 to-transparent"
        aria-hidden="true"
      />

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"
        aria-hidden="true"
      />

      {/* Background grid */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          opacity: 0.4 * visibility,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 14%, black 86%, transparent)",
        }}
      />

      {/* Background glows */}
      <div
        className="pointer-events-none absolute -left-40 top-28 h-80 w-80 rounded-full bg-accent/[0.08] blur-[100px] sm:h-[30rem] sm:w-[30rem] lg:-left-48 lg:top-20 lg:h-[38rem] lg:w-[38rem] lg:blur-[140px]"
        aria-hidden="true"
        style={{
          opacity: visibility,
          transform: `translate3d(${(1 - visibility) * -40}px, ${
            (1 - visibility) * -20
          }px, 0)`,
        }}
      />

      <div
        className="pointer-events-none absolute -right-40 bottom-0 h-80 w-80 rounded-full bg-primary/[0.1] blur-[100px] sm:h-[28rem] sm:w-[28rem] lg:-right-48 lg:h-[34rem] lg:w-[34rem] lg:blur-[140px]"
        aria-hidden="true"
        style={{
          opacity: visibility,
          transform: `translate3d(${(1 - visibility) * 40}px, ${
            (1 - visibility) * 25
          }px, 0)`,
        }}
      />

      {/* Decorative shapes */}
      <div
        className="pointer-events-none absolute right-[6%] top-24 hidden h-20 w-20 rounded-[1.75rem] border border-accent/10 bg-accent/[0.025] sm:block"
        aria-hidden="true"
        style={{
          opacity: visibility,
          transform: `rotate(${12 + visibility * 8}deg)`,
        }}
      />

      <div className={`relative ${spacing.container}`}>
        <div className="grid gap-16 lg:grid-cols-[0.82fr_1.18fr] lg:gap-20 xl:gap-28">
          {/* Introduction */}
          <div className="min-w-0 text-center lg:sticky lg:top-28 lg:self-start lg:text-left">
            <div
              style={{
                opacity: visibility,
                transform: `translateY(${(1 - visibility) * 20}px)`,
              }}
            >
              <p className={`${typography.label} text-accent`}>Why O3S</p>
            </div>

            <h2
              className={`${typography.heading.h2} mx-auto mt-5 max-w-xl text-white lg:mx-0 lg:mt-6`}
              style={{
                opacity: visibility,
                transform: `translateY(${(1 - visibility) * 28}px)`,
              }}
            >
              One partner.
              <br />
              Fewer handoffs.
              <br />
              <span className="font-semibold text-accent">Better work.</span>
            </h2>

            <div
              className="mx-auto mt-6 h-1 w-16 origin-left rounded-full bg-gradient-to-r from-primary to-accent lg:mx-0"
              style={{
                opacity: visibility,
                transform: `scaleX(${visibility})`,
              }}
            />

            <p
              className={`${typography.body.base} mx-auto mt-7 max-w-lg text-white/60 lg:mx-0`}
              style={{
                opacity: visibility,
                transform: `translateY(${(1 - visibility) * 34}px)`,
              }}
            >
              A direct and flexible way to build your digital presence without
              unnecessary layers or complicated processes.
            </p>

            {/* O3S visual */}
            <div
              className="relative mx-auto mt-10 w-full max-w-[280px] sm:mt-12 sm:max-w-[340px] lg:mx-0 lg:max-w-[390px]"
              style={{
                opacity: visibility,
                transform: `translateY(${(1 - visibility) * 35}px) scale(${
                  0.95 + visibility * 0.05
                })`,
              }}
            >
              <div
                className="pointer-events-none absolute inset-8 rounded-full bg-accent/[0.07] blur-3xl"
                aria-hidden="true"
              />

              <div className="relative aspect-square">
                {/* Outer ring */}
                <div
                  className="absolute inset-2 rounded-full border border-white/15 sm:inset-0"
                  style={{
                    transform: `rotate(${visibility * 18}deg)`,
                  }}
                >
                  <div className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent shadow-[0_0_24px_rgba(214,179,106,0.7)] sm:h-3 sm:w-3" />

                  <div className="absolute bottom-[13%] right-[6%] h-2 w-2 rounded-full bg-white/40" />

                  <div className="absolute left-[3%] top-[35%] h-2 w-2 rounded-full bg-primary/60 shadow-[0_0_16px_rgba(230,126,34,0.45)]" />
                </div>

                {/* Middle ring */}
                <div
                  className="absolute inset-[14%] rounded-full border border-dashed border-white/15 sm:inset-[11%]"
                  style={{
                    transform: `rotate(${-visibility * 26}deg)`,
                  }}
                />

                {/* Inner glow */}
                <div
                  className="absolute inset-[22%] rounded-full bg-gradient-to-br from-accent/[0.08] via-white/[0.025] to-primary/[0.08] blur-xl"
                  aria-hidden="true"
                />

                {/* Center */}
                <div className="absolute inset-[25%] flex flex-col items-center justify-center rounded-full border border-accent/20 bg-[#1c2018]/85 px-3 text-center shadow-2xl shadow-black/40 backdrop-blur-md sm:inset-[23%]">
                  <span className="text-4xl font-semibold tracking-[-0.06em] text-white sm:text-5xl lg:text-6xl">
                    O3S
                  </span>

                  <span className="mt-2 max-w-[7rem] text-[8px] font-semibold uppercase leading-relaxed tracking-[0.18em] text-accent/70 sm:mt-3 sm:max-w-[8rem] sm:text-[10px] sm:tracking-[0.22em]">
                    One unified partner
                  </span>
                </div>

                {/* Desktop orbit labels */}
                <span className="absolute right-0 top-[17%] hidden rounded-full border border-white/10 bg-[#171a14]/90 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55 shadow-lg backdrop-blur-md sm:block">
                  Online
                </span>

                <span className="absolute bottom-[13%] left-0 hidden rounded-full border border-white/10 bg-[#171a14]/90 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55 shadow-lg backdrop-blur-md sm:block">
                  International
                </span>
              </div>

              {/* Mobile labels */}
              <div className="mt-5 flex justify-center gap-2 sm:hidden">
                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white/50">
                  Online
                </span>

                <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-white/50">
                  International
                </span>
              </div>
            </div>
          </div>

          {/* Feature list */}
          <div
            className="relative min-w-0 overflow-hidden rounded-[1.75rem] border border-white/10 bg-white/[0.025] p-1.5 shadow-[0_30px_90px_rgba(0,0,0,0.22)] backdrop-blur-sm sm:rounded-[2.25rem] sm:p-2"
            style={{
              opacity: visibility,
              transform: `translateY(${(1 - visibility) * 30}px)`,
            }}
          >
            <div
              className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/[0.08] blur-3xl"
              aria-hidden="true"
            />

            <div
              className="pointer-events-none absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-accent/[0.06] blur-3xl"
              aria-hidden="true"
            />

            <div className="relative">
              {whyO3SItems.map((item, index) => {
                const Icon = item.icon;

                const itemVisibility = clamp(
                  (visibility - index * 0.08) / 0.68,
                );

                return (
                  <article
                    key={item.title}
                    className="group relative overflow-hidden border-b border-white/10 px-4 py-6 last:border-b-0 sm:px-6 sm:py-8 lg:px-6 lg:py-10"
                    style={{
                      opacity: itemVisibility,
                      transform: `translate3d(0, ${
                        (1 - itemVisibility) * 24
                      }px, 0)`,
                      transition:
                        "transform 100ms linear, opacity 160ms linear",
                    }}
                  >
                    {/* Hover background */}
                    <div
                      className="pointer-events-none absolute inset-1 rounded-[1.5rem] bg-gradient-to-r from-accent/[0.09] via-primary/[0.035] to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 sm:rounded-[1.75rem]"
                      aria-hidden="true"
                    />

                    {/* Accent line */}
                    <div
                      className="absolute bottom-5 left-0 top-5 hidden w-[3px] origin-center scale-y-0 rounded-full bg-gradient-to-b from-primary to-accent transition-transform duration-500 group-hover:scale-y-100 sm:block"
                      aria-hidden="true"
                    />

                    <div className="relative grid min-w-0 grid-cols-[48px_minmax(0,1fr)] gap-x-4 gap-y-3 lg:grid-cols-[72px_minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-start lg:gap-x-7">
                      {/* Number and icon */}
                      <div className="row-span-2 lg:row-span-1">
                        <span className="block text-[11px] font-semibold tracking-[0.16em] text-accent/55 transition-colors duration-300 group-hover:text-accent sm:text-sm sm:tracking-[0.18em]">
                          {item.number}
                        </span>

                        <div className="mt-2.5 flex h-11 w-11 items-center justify-center rounded-xl border border-accent/15 bg-accent/[0.08] text-accent transition-all duration-500 group-hover:-translate-y-1 group-hover:rotate-6 group-hover:scale-105 group-hover:border-accent/30 group-hover:bg-accent/15 sm:mt-4 sm:h-12 sm:w-12 sm:rounded-2xl">
                          <Icon size={18} aria-hidden="true" />
                        </div>
                      </div>

                      {/* Title */}
                      <div className="min-w-0 self-center lg:self-start">
                        <h3
                          className={`${typography.card.large} break-words text-white transition-colors duration-300 group-hover:text-accent`}
                        >
                          {item.title}
                        </h3>
                      </div>

                      {/* Description */}
                      <div className="min-w-0">
                        <p
                          className={`${typography.body.base} break-words text-white/55 transition-colors duration-300 group-hover:text-white/75`}
                        >
                          {item.text}
                        </p>
                      </div>
                    </div>

                    {/* Faded number */}
                    <span
                      className="pointer-events-none absolute -bottom-5 right-2 select-none text-[5rem] font-semibold leading-none text-white/[0.018] transition-all duration-700 group-hover:-translate-x-2 group-hover:text-accent/[0.05] sm:-bottom-7 sm:right-3 sm:text-[7rem]"
                      aria-hidden="true"
                    >
                      {item.number}
                    </span>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyO3S;
